import { useParams, Navigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/base/Seo';
import { caseStudies } from '@/mocks/caseStudies';

const siteUrl =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) ||
  'https://biffdigital.com';
const logoUrl =
  'https://storage.helloreaddy.io/project_files/8cf86b48-d09c-4ec8-816e-12bbed2b078b/1e3fa9b9-1413-4092-84fc-01303456e9e9_compressed_Biff-Digital-Logo.webp';

export default function VakaDetay() {
  const { slug } = useParams<{ slug: string }>();
  const study = caseStudies.find((c) => c.slug === slug);

  if (!study) {
    return <Navigate to="/basarili-dijital-projeler" replace />;
  }

  const pageUrl = `${siteUrl}/basarili-dijital-projeler/${study.slug}`;
  const idx = caseStudies.findIndex((c) => c.slug === slug);
  const prev = caseStudies[idx - 1];
  const next = caseStudies[idx + 1];

  const seoDescription =
    study.problem.length > 150
      ? `${study.problem.slice(0, 150)}...`
      : study.problem;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: study.title,
        description: `${study.problem} ${study.result}`,
        mainEntityOfPage: pageUrl,
        author: {
          '@type': 'Person',
          name: 'BIFF Digital Strateji Ekibi',
          jobTitle: 'Dijital Pazarlama Uzmanı',
          worksFor: { '@type': 'Organization', name: 'BIFF Digital', url: siteUrl },
        },
        publisher: {
          '@type': 'Organization',
          name: 'BIFF Digital',
          logo: { '@type': 'ImageObject', url: logoUrl },
        },
        datePublished: study.datePublished,
        dateModified: '2026-08-07',
        about: study.category,
        inLanguage: 'tr',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Vaka Çalışmaları', item: `${siteUrl}/basarili-dijital-projeler` },
          { '@type': 'ListItem', position: 3, name: study.title, item: pageUrl },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title={`${study.title} | Vaka Çalışması - BIFF Digital`}
        description={seoDescription}
        path={`/basarili-dijital-projeler/${study.slug}`}
        type="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors">Ana Sayfa</Link>
                </li>
                <li className="text-foreground-600">/</li>
                <li>
                  <Link to="/basarili-dijital-projeler" className="hover:text-primary-500 transition-colors">
                    Vaka Çalışmaları
                  </Link>
                </li>
                <li className="text-foreground-600">/</li>
                <li className="text-foreground-300 font-medium">{study.title}</li>
              </ol>
            </nav>

            {/* Header */}
            <div className="mb-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`inline-block px-4 py-2 rounded-lg bg-gradient-to-r ${study.gradient} text-foreground-50`}>
                  <span className="text-xs font-bold uppercase tracking-wider">{study.category}</span>
                </span>
                <span className="text-xs font-bold text-foreground-500">
                  {new Date(study.datePublished).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })}
                </span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-50 tracking-tighter leading-tight">
                {study.title}
              </h1>
            </div>

            {/* Methodology */}
            <div className="glass-card rounded-2xl p-6 md:p-10">
              {/* Problem + Insight */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h2 className="flex items-center gap-2 text-xs md:text-sm font-bold text-primary-500 uppercase tracking-wider mb-3">
                    <i className="ri-alert-line text-base" />
                    Problem
                  </h2>
                  <p className="text-sm md:text-base text-foreground-300 leading-relaxed">{study.problem}</p>
                </div>
                <div className="bg-accent-500/5 border border-accent-500/20 rounded-xl p-5">
                  <h2 className="flex items-center gap-2 text-xs md:text-sm font-bold text-accent-500 uppercase tracking-wider mb-3">
                    <i className="ri-lightbulb-line text-base" />
                    Insight
                  </h2>
                  <p className="text-sm md:text-base text-foreground-300 leading-relaxed">{study.insight}</p>
                </div>
              </div>

              {/* Strategy + Execution */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h2 className="flex items-center gap-2 text-xs md:text-sm font-bold text-primary-500 uppercase tracking-wider mb-3">
                    <i className="ri-compass-3-line text-base" />
                    Strategy
                  </h2>
                  <p className="text-sm md:text-base text-foreground-300 leading-relaxed">{study.strategy}</p>
                </div>
                <div>
                  <h2 className="flex items-center gap-2 text-xs md:text-sm font-bold text-primary-500 uppercase tracking-wider mb-3">
                    <i className="ri-settings-3-line text-base" />
                    Execution
                  </h2>
                  <p className="text-sm md:text-base text-foreground-300 leading-relaxed">{study.execution}</p>
                </div>
              </div>

              {/* Result */}
              <div className="bg-primary-500/10 border-l-4 border-primary-500 rounded-xl p-5 mb-6">
                <h2 className="flex items-center gap-2 text-xs md:text-sm font-bold text-primary-500 uppercase tracking-wider mb-3">
                  <i className="ri-line-chart-line text-base" />
                  Result
                </h2>
                <p className="text-sm md:text-base text-foreground-100 font-medium leading-relaxed">{study.result}</p>
              </div>

              {/* What We Learned */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-background-100 rounded-lg flex items-center justify-center shrink-0">
                  <i className="ri-book-open-line text-lg text-secondary-500" />
                </div>
                <div>
                  <h2 className="text-xs md:text-sm font-bold text-secondary-500 uppercase tracking-wider mb-1">
                    What We Learned
                  </h2>
                  <p className="text-sm text-foreground-400 leading-relaxed italic">
                    &ldquo;{study.learning}&rdquo;
                  </p>
                </div>
              </div>
            </div>

            {/* Prev / Next */}
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {prev ? (
                <Link
                  to={`/basarili-dijital-projeler/${prev.slug}`}
                  className="glass-card-hover rounded-xl p-5 flex items-center gap-3 cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-primary-500 text-xl shrink-0" />
                  <div>
                    <p className="text-xs text-foreground-500 uppercase tracking-wider mb-1">Önceki Vaka</p>
                    <p className="text-sm font-bold text-foreground-50">{prev.title}</p>
                  </div>
                </Link>
              ) : <div />}
              {next ? (
                <Link
                  to={`/basarili-dijital-projeler/${next.slug}`}
                  className="glass-card-hover rounded-xl p-5 flex items-center justify-end gap-3 text-right cursor-pointer"
                >
                  <div>
                    <p className="text-xs text-foreground-500 uppercase tracking-wider mb-1">Sonraki Vaka</p>
                    <p className="text-sm font-bold text-foreground-50">{next.title}</p>
                  </div>
                  <i className="ri-arrow-right-line text-primary-500 text-xl shrink-0" />
                </Link>
              ) : <div />}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-6 md:p-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
                Sıradaki Başarı Hikayesi Senin Olabilir
              </h2>
              <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto">
                Bu vakada olduğu gibi, markanın kırılma noktasını bulup büyümeye çevirebiliriz. Hemen iletişime geç.
              </p>
              <a href="/#contact" className="btn-primary">
                Ücretsiz Danışmanlık
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}